/** @jsx jsx */
/* *
 * * Component Name: App
 * Project: Driev
 * Description: Entry point to the application being used in index root
 * Author: Pranatosh Roy
 * Date Created: 20-April-2022
 * Last Modified On
 * Remarks:
 */
/** ------------------ Primary Import Declaration -------- */
import React, { useContext, useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { css, jsx } from "@emotion/react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
/** ------------------ MUI Components & APIs ------------- */
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import CssBaseline from "@mui/material/CssBaseline";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Drawer from "@mui/material/Drawer";
import Collapse from "@mui/material/Collapse";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Button from "@mui/material/Button";
import Skeleton from "@mui/material/Skeleton";
import Avatar from "@mui/material/Avatar";
/** ------------------ Icons ----------------------------- */
import MenuIcon from "@mui/icons-material/Menu";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import LocalMallRoundedIcon from "@mui/icons-material/LocalMallRounded";
import StarIcon from "@mui/icons-material/Star";
import BusinessIcon from "@mui/icons-material/Business";
/** ------------------ User Components ------------------- */
import { postRecord } from "services/services";
import { ThemeContext } from "context/ThemeContext";
import { AuthContext } from "context/Authentication";
import Branding from "./sections/Branding.jsx";
import HeaderMenu from "./sections/HeaderMenu.jsx";
import HeaderActions from "./sections/HeaderActions.jsx";
import TopHeader from "./TopHeader.jsx";
import SearchComponent from "CommonComponents/Search/SearchComponent.jsx";
/** ------------------ Media Imports --------------------- */
import commonStyles from "templates/default/assets/styles/commonstyles/commonStyles";
/** ------------------ API Declarations ------------------ */
const API_GetAllCategories = "productcategories/getall";
const API_GetAllSubcategories = "productsubcategories/getall";
/** ------------------ Third Party Components ------------ */
/** ------------------ Initial States -------------------- */

export default function Header({ headerRefresh, setHeaderRefresh, ...props }) {
  /** ------------------ useContext ------------------------- */
  const themeMode = useContext(ThemeContext);
  const darkMode = themeMode.state.darkMode;
  const { dispatch } = useContext(AuthContext);
  const context = useContext(AuthContext);
  const anchorRef = useRef(null);
  const location = useLocation();
  let navigate = useNavigate();

  /** ------------------ useStates ------------------------- */
  const [anchorElNav, setAnchorElNav] = useState(null);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [openCategory, setOpenCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [images, setImages] = useState([]);
  const [scrollY, setScrollY] = useState(0);

  /** ------------------ useEffects ------------------------ */
  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        // Fetch categories
        const catResponse = await postRecord(API_GetAllCategories, {});
        if (catResponse.data && isMounted) {
          const filteredCategories = catResponse.data.filter(
            (category) => category.On_Mainmenu == 1
          );

          if (filteredCategories.length > 0 && isMounted) {
            const catImgs = [];
            filteredCategories.forEach((category) => {
              const j = category.Category_Media_Files
                ? JSON.parse(category.Category_Media_Files)
                : [];
              j.forEach((img) => {
                catImgs.push(process.env.BASEURL + img);
              });
            });
            setImages(catImgs);
          }
          if (isMounted) setCategories(filteredCategories);
        }

        // Fetch subcategories
        const subResponse = await postRecord(API_GetAllSubcategories, {});
        if (subResponse.data && isMounted) {
          const filteredSubcategories = subResponse.data.filter(
            (subcategory) => subcategory.On_Mainmenu == 1
          );
          if (isMounted) setSubcategories(filteredSubcategories);
        }
      } catch (error) {
        if (isMounted) {
          console.error("Error fetching header data:", error);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const interval = setInterval(() => {
      if (!isMounted) {
        clearInterval(interval);
        return;
      }

      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 3000); // Change image every 3 seconds

    return () => {
      isMounted = false;
      clearInterval(interval); // Cleanup interval on component unmount
    };
  }, [images.length]);

  /** ------------------ Functions & Events ---------------- */
  const handleMenuClick = () => setDropdownOpen(!dropdownOpen);
  const handleCategoryClick = (id) => {
    setOpenCategory(openCategory === id ? null : id); // Toggle open/close for a category
  };

  const handleMenuOpen = (event) => {
    setMobileMenu(true);
  };
  const handleManuClose = () => {
    setMobileMenu(false);
  };

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };
  const handleLinkClick = (categoryObj, subcategoryObj) => {
    if (categoryObj) {
      // Check if we're dealing with objects or legacy string parameters
      const category =
        typeof categoryObj === "object"
          ? categoryObj
          : { Category_Name: categoryObj, Category_Type: "Product" };
      const subcategory =
        typeof subcategoryObj === "object"
          ? subcategoryObj
          : subcategoryObj
          ? { Sub_Category_Name: subcategoryObj }
          : null;

      const formattedCategoryName =
        category.Category_Name.toLowerCase().replace(/ /g, "-");

      // Determine the base route based on Category_Type
      const baseRoute =
        category.Category_Type === "Service" ? "/ServiceList" : "/ProductList";

      if (subcategory) {
        const formattedSubCategoryName =
          subcategory.Sub_Category_Name.toLowerCase().replace(/ /g, "-");
        navigate(
          `${baseRoute}/${formattedCategoryName}/${formattedSubCategoryName}`
        );
      } else {
        navigate(`${baseRoute}/${formattedCategoryName}`);
      }
    }
  };

  // Determine Collated Main Menu setting
  const collatedMenuSetting = Array.isArray(context.state?.appSettings)
    ? context.state.appSettings.find(
        (s) => s.Setting_Name === "Collated Main Menu"
      )
    : null;
  const isCollatedMenu = collatedMenuSetting
    ? collatedMenuSetting.Setting_Value === "YES"
    : true; // default to YES if not set

  // Determine Menu Display Text setting
  const menuDisplayTextSetting = Array.isArray(context.state?.appSettings)
    ? context.state.appSettings.find(
        (s) => s.Setting_Name === "Menu Display Text"
      )
    : null;
  const menuDisplayText = menuDisplayTextSetting
    ? menuDisplayTextSetting.Setting_Value
    : "Shop"; // default to "Shop" if not set

  // Determine icon based on menu text
  const menuIcon =
    menuDisplayText === "Shop" ? <LocalMallRoundedIcon /> : <MenuIcon />;

  // Helper: get first image from media JSON
  const getFirstImage = (media) => {
    if (!media) return null;
    let arr = [];
    try {
      arr = typeof media === "string" ? JSON.parse(media) : media;
    } catch (e) {
      return null;
    }
    if (Array.isArray(arr) && arr.length > 0 && typeof arr[0] === "string") {
      return arr[0].startsWith("http")
        ? arr[0]
        : `${process.env.BASEURL}${arr[0]}`;
    }
    return null;
  };

  return (
    <React.Fragment>
      <CssBaseline />
      <AppBar></AppBar>
      <AppBar color="common" position="fixed">
        <Container maxWidth="xl">
          {Array.isArray(context.state?.appSettings) &&
            context.state.appSettings.filter(
              (s) =>
                s.Setting_Name === "Top Bar" && s.Setting_Value === "Enabled"
            ).length > 0 && <TopHeader />}
          <ClickAwayListener
            onClickAway={() => setDropdownOpen(false)}
            mouseEvent="onMouseDown"
            touchEvent="onTouchStart"
          >
            <Toolbar>
              <Branding />

              {/* Search Component - Always visible */}
              <Box
                sx={{
                  flex: 1,
                  display: { xs: "none", md: "flex" },
                  justifyContent: "center",
                  alignItems: "center",
                  mx: 2,
                }}
              >
                <Box sx={{ maxWidth: "500px", width: "100%" }}>
                  <SearchComponent
                    placeholder="Search for products, categories, services..."
                    variant="outlined"
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "25px",
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.05)"
                            : "rgba(0, 0, 0, 0.02)",
                        "& fieldset": {
                          borderColor: (theme) =>
                            theme.palette.mode === "dark"
                              ? "rgba(255, 255, 255, 0.2)"
                              : "rgba(0, 0, 0, 0.1)",
                        },
                        "&:hover": {
                          backgroundColor: (theme) =>
                            theme.palette.mode === "dark"
                              ? "rgba(255, 255, 255, 0.08)"
                              : "rgba(0, 0, 0, 0.04)",
                        },
                        "&.Mui-focused": {
                          backgroundColor: (theme) =>
                            theme.palette.mode === "dark"
                              ? "rgba(255, 255, 255, 0.1)"
                              : "rgba(255, 255, 255, 1)",
                        },
                      },
                    }}
                    showRecentSearches={true}
                    maxResults={6}
                  />
                </Box>
              </Box>

              {/* Mobile menu */}
              <Box sx={{ flexGrow: 1, display: { xs: "flex", md: "none" } }}>
                <IconButton
                  size="large"
                  aria-label="account of current user"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleMenuOpen}
                  color="inherit"
                >
                  <MenuIcon />
                </IconButton>
                <Drawer
                  open={mobileMenu}
                  onClose={handleManuClose}
                  sx={{ display: { xs: "block", md: "none" } }}
                >
                  <Box sx={{ width: 300 }} role="presentation">
                    <Box
                      sx={{
                        padding: "10px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Link to="/">
                        {context.state.companyInfo &&
                        Object.entries(context.state.companyInfo).length > 0 ? (
                          <img
                            alt={context.state.companyInfo.Company_Name}
                            src={
                              darkMode
                                ? process.env.BASEURL +
                                  context.state.companyInfo.Company_Logo_Light
                                : process.env.BASEURL +
                                  context.state.companyInfo.Company_Logo_Dark
                            }
                            style={{
                              height: "60px",
                              objectFit: "contain",
                            }}
                          />
                        ) : (
                          <Skeleton variant="circular">
                            <Avatar
                              sx={{
                                width: 65,
                                height: 65,
                                marginLeft: "auto",
                                marginRight: "auto",
                              }}
                            />
                          </Skeleton>
                        )}
                      </Link>
                    </Box>

                    {/* Mobile Search Component */}
                    <Box sx={{ px: 2, pb: 2 }}>
                      <SearchComponent
                        placeholder="Search for products, categories, services..."
                        variant="outlined"
                        size="small"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "25px",
                            backgroundColor: (theme) =>
                              theme.palette.mode === "dark"
                                ? "rgba(255, 255, 255, 0.05)"
                                : "rgba(0, 0, 0, 0.02)",
                            "& fieldset": {
                              borderColor: (theme) =>
                                theme.palette.mode === "dark"
                                  ? "rgba(255, 255, 255, 0.2)"
                                  : "rgba(0, 0, 0, 0.1)",
                            },
                            "&:hover": {
                              backgroundColor: (theme) =>
                                theme.palette.mode === "dark"
                                  ? "rgba(255, 255, 255, 0.08)"
                                  : "rgba(0, 0, 0, 0.04)",
                            },
                            "&.Mui-focused": {
                              backgroundColor: (theme) =>
                                theme.palette.mode === "dark"
                                  ? "rgba(255, 255, 255, 0.1)"
                                  : "rgba(255, 255, 255, 1)",
                            },
                          },
                        }}
                        showRecentSearches={true}
                        maxResults={6}
                      />
                    </Box>

                    <Box>
                      <List>
                        <ListItem disablePadding>
                          <ListItemButton
                            fullWidth
                            onClick={() => navigate("/")}
                          >
                            <ListItemIcon>
                              <HomeRoundedIcon />
                            </ListItemIcon>
                            <ListItemText primary="Home" />
                          </ListItemButton>
                          <Divider />
                        </ListItem>
                        {/* About page link in mobile drawer (conditionally after Home) */}
                        {Array.isArray(context.state?.appSettings) &&
                          context.state.appSettings.some(
                            (s) =>
                              s.Setting_Name === "About Enabled" &&
                              s.Setting_Value === "Yes"
                          ) && (
                            <ListItem disablePadding>
                              <ListItemButton
                                fullWidth
                                onClick={() => navigate("/About")}
                              >
                                <ListItemIcon>
                                  <BusinessIcon />
                                </ListItemIcon>
                                <ListItemText primary="About" />
                              </ListItemButton>
                              <Divider />
                            </ListItem>
                          )}
                        {/* Subscriptions in mobile drawer (always after Home/About, if enabled in appSettings) */}
                        {Array.isArray(context.state?.appSettings) &&
                          context.state.appSettings.some(
                            (s) =>
                              s.Setting_Name === "Subscriptions" &&
                              s.Setting_Value === "Enabled"
                          ) && (
                            <ListItem disablePadding>
                              <ListItemButton
                                fullWidth
                                onClick={() => navigate("/SubscriptionList")}
                              >
                                <ListItemIcon>
                                  <StarIcon />
                                </ListItemIcon>
                                <ListItemText primary="Subscriptions" />
                              </ListItemButton>
                              <Divider />
                            </ListItem>
                          )}
                        {/* Collated Main Menu logic for mobile */}
                        {isCollatedMenu ? (
                          <>
                            <ListItem disablePadding>
                              <ListItemButton
                                fullWidth
                                onClick={handleMenuClick}
                              >
                                <ListItemIcon>{menuIcon}</ListItemIcon>
                                <ListItemText primary={menuDisplayText} />
                                {dropdownOpen ? <ExpandLess /> : <ExpandMore />}
                              </ListItemButton>
                              <Divider />
                            </ListItem>
                            <Collapse
                              in={dropdownOpen}
                              timeout="auto"
                              unmountOnExit
                            >
                              <List component="div" disablePadding>
                                {categories.map((category) => (
                                  <Box key={category.Category_Id}>
                                    <ListItemButton
                                      onClick={() => {
                                        // If category has subcategories, toggle expand/collapse
                                        if (
                                          subcategories.some(
                                            (sub) =>
                                              sub.Category_Id ===
                                              category.Category_Id
                                          )
                                        ) {
                                          handleCategoryClick(
                                            category.Category_Id
                                          );
                                        } else {
                                          // If no subcategories, navigate directly to category
                                          handleLinkClick(category, null);
                                          setMobileMenu(false); // Close mobile menu
                                        }
                                      }}
                                    >
                                      <ListItemText
                                        primary={category.Category_Name}
                                        primaryTypographyProps={{
                                          fontWeight: "bold",
                                        }}
                                      />
                                      {subcategories.some(
                                        (sub) =>
                                          sub.Category_Id ===
                                          category.Category_Id
                                      ) ? (
                                        openCategory ===
                                        category.Category_Id ? (
                                          <ExpandLess />
                                        ) : (
                                          <ExpandMore />
                                        )
                                      ) : null}
                                    </ListItemButton>
                                    {subcategories.some(
                                      (sub) =>
                                        sub.Category_Id === category.Category_Id
                                    ) && (
                                      <Collapse
                                        in={
                                          openCategory === category.Category_Id
                                        }
                                        timeout="auto"
                                        unmountOnExit
                                      >
                                        <List component="div" disablePadding>
                                          <ListItemButton
                                            sx={{
                                              padding: "5px 10px",
                                              justifyContent: "flex-end",
                                              fontSize: "14px",
                                            }}
                                            onClick={() => {
                                              handleLinkClick(category, null);
                                              setMobileMenu(false); // Close mobile menu
                                            }}
                                          >
                                            Explore
                                          </ListItemButton>
                                          {subcategories
                                            .filter(
                                              (sub) =>
                                                sub.Category_Id ===
                                                category.Category_Id
                                            )
                                            .map((subcategory, index) => (
                                              <ListItemButton
                                                key={index}
                                                sx={{
                                                  padding: "5px 10px",
                                                  textIndent: "20px",
                                                  fontSize: "14px",
                                                }}
                                                onClick={() => {
                                                  handleLinkClick(
                                                    category,
                                                    subcategory
                                                  );
                                                  setMobileMenu(false); // Close mobile menu
                                                }}
                                              >
                                                {subcategory.Sub_Category_Name}
                                              </ListItemButton>
                                            ))}
                                        </List>
                                      </Collapse>
                                    )}
                                  </Box>
                                ))}
                              </List>
                            </Collapse>
                          </>
                        ) : (
                          // Collated Main Menu = NO: show categories as top-level, subcategories as dropdowns with images
                          <>
                            {categories.map((category) => {
                              let catImg = null;
                              if (category.Category_Media_Files) {
                                try {
                                  const imgs = JSON.parse(
                                    category.Category_Media_Files
                                  );
                                  if (imgs.length > 0)
                                    catImg = process.env.BASEURL + imgs[0];
                                } catch {}
                              }
                              return (
                                <Box key={category.Category_Id}>
                                  <ListItemButton
                                    onClick={() => {
                                      // If category has subcategories, toggle expand/collapse
                                      if (
                                        subcategories.some(
                                          (sub) =>
                                            sub.Category_Id ===
                                            category.Category_Id
                                        )
                                      ) {
                                        handleCategoryClick(
                                          category.Category_Id
                                        );
                                      } else {
                                        // If no subcategories, navigate directly to category
                                        handleLinkClick(category, null);
                                        setMobileMenu(false); // Close mobile menu
                                      }
                                    }}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 2,
                                    }}
                                  >
                                    {catImg && (
                                      <img
                                        src={catImg}
                                        alt={category.Category_Name}
                                        style={{
                                          width: 36,
                                          height: 36,
                                          borderRadius: 8,
                                          objectFit: "cover",
                                          marginRight: 8,
                                        }}
                                      />
                                    )}
                                    <ListItemText
                                      primary={category.Category_Name}
                                      primaryTypographyProps={{
                                        fontWeight: "bold",
                                      }}
                                    />
                                    {subcategories.some(
                                      (sub) =>
                                        sub.Category_Id === category.Category_Id
                                    ) ? (
                                      openCategory === category.Category_Id ? (
                                        <ExpandLess />
                                      ) : (
                                        <ExpandMore />
                                      )
                                    ) : null}
                                  </ListItemButton>
                                  {subcategories.some(
                                    (sub) =>
                                      sub.Category_Id === category.Category_Id
                                  ) && (
                                    <Collapse
                                      in={openCategory === category.Category_Id}
                                      timeout="auto"
                                      unmountOnExit
                                    >
                                      <List component="div" disablePadding>
                                        {subcategories
                                          .filter(
                                            (sub) =>
                                              sub.Category_Id ===
                                              category.Category_Id
                                          )
                                          .map((subcategory, idx) => {
                                            let subImg = null;
                                            if (
                                              subcategory.SubCategory_Media_Files
                                            ) {
                                              try {
                                                const imgs = JSON.parse(
                                                  subcategory.SubCategory_Media_Files
                                                );
                                                if (imgs.length > 0)
                                                  subImg =
                                                    process.env.BASEURL +
                                                    imgs[0];
                                              } catch {}
                                            }
                                            return (
                                              <ListItemButton
                                                key={idx}
                                                sx={{
                                                  padding: "5px 10px",
                                                  textIndent: "20px",
                                                  fontSize: "14px",
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: 1.5,
                                                }}
                                                onClick={() => {
                                                  handleLinkClick(
                                                    category,
                                                    subcategory
                                                  );
                                                  setMobileMenu(false); // Close mobile menu
                                                }}
                                              >
                                                {subImg && (
                                                  <img
                                                    src={subImg}
                                                    alt={
                                                      subcategory.Sub_Category_Name
                                                    }
                                                    style={{
                                                      width: 28,
                                                      height: 28,
                                                      borderRadius: 6,
                                                      objectFit: "cover",
                                                      marginRight: 8,
                                                    }}
                                                  />
                                                )}
                                                {subcategory.Sub_Category_Name}
                                              </ListItemButton>
                                            );
                                          })}
                                      </List>
                                    </Collapse>
                                  )}
                                </Box>
                              );
                            })}
                          </>
                        )}
                      </List>
                    </Box>
                  </Box>
                </Drawer>
              </Box>
              {/* Desktop menu logic */}
              <Box
                sx={{
                  my: 1,
                  display: { xs: "none", md: "flex" },
                  alignItems: "center",
                }}
              >
                <Button
                  ref={anchorRef}
                  startIcon={<HomeRoundedIcon />}
                  variant="link"
                  color="neutralText"
                  onClick={() => navigate("/")}
                >
                  Home
                </Button>
                {/* About page link (conditionally after Home) */}
                {Array.isArray(context.state?.appSettings) &&
                  context.state.appSettings.some(
                    (s) =>
                      s.Setting_Name === "About Enabled" &&
                      s.Setting_Value === "Yes"
                  ) && (
                    <Button
                      startIcon={<BusinessIcon />}
                      variant="link"
                      color="neutralText"
                      onClick={() => navigate("/About")}
                      sx={{ ml: 1 }}
                    >
                      About
                    </Button>
                  )}
                {/* Subscriptions after Home/About */}
                {Array.isArray(context.state?.appSettings) &&
                  context.state.appSettings.some(
                    (s) =>
                      s.Setting_Name === "Subscriptions" &&
                      s.Setting_Value === "Enabled"
                  ) && (
                    <Button
                      startIcon={<StarIcon />}
                      variant="link"
                      color="neutralText"
                      onClick={() => navigate("/SubscriptionList")}
                      sx={{ ml: 1 }}
                    >
                      Subscriptions
                    </Button>
                  )}
                {/* Shop or categories as last item */}
                {isCollatedMenu ? (
                  <>
                    <Button
                      ref={anchorRef}
                      startIcon={menuIcon}
                      variant="link"
                      color="neutralText"
                      onClick={handleMenuClick}
                      endIcon={<KeyboardArrowDownIcon />}
                    >
                      {menuDisplayText}
                    </Button>
                    {dropdownOpen && (
                      <HeaderMenu
                        categories={categories}
                        subcategories={subcategories}
                        images={images}
                        currentIndex={currentIndex}
                        darkMode={darkMode}
                        handleLinkClick={handleLinkClick}
                      />
                    )}
                  </>
                ) : (
                  // Collated Main Menu = NO: show categories as top-level, subcategories as dropdowns with images
                  <>
                    {categories.map((category) => {
                      let catImg = null;
                      if (category.Category_Media_Files) {
                        try {
                          const imgs = JSON.parse(
                            category.Category_Media_Files
                          );
                          if (imgs.length > 0)
                            catImg = process.env.BASEURL + imgs[0];
                        } catch {}
                      }
                      return (
                        <Box
                          key={category.Category_Id}
                          sx={{ position: "relative" }}
                        >
                          <Button
                            variant="link"
                            color="neutralText"
                            startIcon={
                              catImg ? (
                                <img
                                  src={catImg}
                                  alt={category.Category_Name}
                                  style={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: 8,
                                    objectFit: "cover",
                                    marginRight: 8,
                                  }}
                                />
                              ) : null
                            }
                            endIcon={
                              subcategories.some(
                                (sub) =>
                                  sub.Category_Id === category.Category_Id
                              ) ? (
                                <KeyboardArrowDownIcon />
                              ) : null
                            }
                            onClick={() =>
                              handleCategoryClick(category.Category_Id)
                            }
                            sx={{
                              fontWeight: "bold",
                              mx: 1,
                              px: 2,
                              py: 1.5,
                              borderRadius: 2,
                              transition: "background 0.2s",
                              "&:hover": {
                                background: darkMode ? "#232323" : "#f5f5f5",
                              },
                            }}
                          >
                            {category.Category_Name}
                          </Button>
                          {subcategories.some(
                            (sub) => sub.Category_Id === category.Category_Id
                          ) &&
                            openCategory === category.Category_Id && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: "100%",
                                  left: 0,
                                  minWidth: 220,
                                  bgcolor: darkMode ? "#232323" : "#fff",
                                  boxShadow: 3,
                                  borderRadius: 2,
                                  zIndex: 1200,
                                  mt: 1,
                                  p: 1,
                                }}
                              >
                                {/* Explore Category Button */}
                                <Button
                                  variant="contained"
                                  color="primary"
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: "100%",
                                    textAlign: "center",
                                    px: 2,
                                    py: 1.5,
                                    borderRadius: 1.5,
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    mb: 1,
                                    textTransform: "none",
                                  }}
                                  onClick={() => handleLinkClick(category)}
                                >
                                  Explore {category.Category_Name}
                                </Button>

                                {/* Divider */}
                                <Box
                                  sx={{
                                    width: "100%",
                                    height: 1,
                                    bgcolor: darkMode ? "#444" : "#e0e0e0",
                                    mb: 1,
                                  }}
                                />

                                {subcategories
                                  .filter(
                                    (sub) =>
                                      sub.Category_Id === category.Category_Id
                                  )
                                  .map((subcategory, idx) => {
                                    let subImg = null;
                                    if (subcategory.SubCategory_Media_Files) {
                                      try {
                                        const imgs = JSON.parse(
                                          subcategory.SubCategory_Media_Files
                                        );
                                        if (imgs.length > 0)
                                          subImg =
                                            process.env.BASEURL + imgs[0];
                                      } catch {}
                                    }
                                    return (
                                      <Button
                                        key={idx}
                                        variant="link"
                                        color="neutralText"
                                        startIcon={
                                          subImg ? (
                                            <img
                                              src={subImg}
                                              alt={
                                                subcategory.Sub_Category_Name
                                              }
                                              style={{
                                                width: 28,
                                                height: 28,
                                                borderRadius: 6,
                                                objectFit: "cover",
                                                marginRight: 8,
                                              }}
                                            />
                                          ) : null
                                        }
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "flex-start",
                                          width: "100%",
                                          textAlign: "left",
                                          px: 2,
                                          py: 1,
                                          borderRadius: 1.5,
                                          fontSize: "15px",
                                          fontWeight: 500,
                                          mb: 0.5,
                                          transition: "background 0.2s",
                                          "&:hover": {
                                            background: darkMode
                                              ? "#232323"
                                              : "#f5f5f5",
                                          },
                                        }}
                                        onClick={() =>
                                          handleLinkClick(category, subcategory)
                                        }
                                      >
                                        {subcategory.Sub_Category_Name}
                                      </Button>
                                    );
                                  })}
                              </Box>
                            )}
                        </Box>
                      );
                    })}
                  </>
                )}
              </Box>
              <HeaderActions
                headerRefresh={headerRefresh}
                setHeaderRefresh={setHeaderRefresh}
              />
            </Toolbar>
          </ClickAwayListener>
        </Container>
      </AppBar>
    </React.Fragment>
  );
}
